import React, { useState, useEffect } from 'react';
import {
  TestPackage,
  CreateTestPackageData,
  getAllTestPackages,
  createTestPackage,
  updateTestPackage,
  toggleTestPackageStatus,
  deleteTestPackage
} from '../../services/testPackageService';

const TestPackages: React.FC = () => {
  const [testPackages, setTestPackages] = useState<TestPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newPackage, setNewPackage] = useState<CreateTestPackageData>({
    name: '',
    description: '',
    price: 0,
    discountPrice: 0,
    category: 'common',
    priority: 1,
    active: true
  });

  const [editingPackage, setEditingPackage] = useState<string | null>(null);
  const [showPackageForm, setShowPackageForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load test packages on component mount
  useEffect(() => {
    loadTestPackages();
  }, []);

  const loadTestPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const packages = await getAllTestPackages();
      setTestPackages(packages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در بارگذاری پکیج ها');

    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (editingPackage !== null) {
      // Update the package being edited
      setTestPackages(testPackages.map(pkg => 
        pkg._id === editingPackage 
          ? { 
              ...pkg, 
              [name]: name === 'active' 
                ? value === 'true' 
                : (name === 'price' || name === 'discountPrice' || name === 'priority') 
                  ? parseInt(value) || 0
                  : value 
            }
          : pkg
      ));
    } else {
      // Update the new package
      setNewPackage({
        ...newPackage,
        [name]: name === 'active' 
          ? value === 'true' 
          : (name === 'price' || name === 'discountPrice' || name === 'priority') 
            ? parseInt(value) || 0
            : value
      } as CreateTestPackageData);
    }
  };

  const addOrUpdatePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (submitting) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      if (editingPackage !== null) {
        // Update existing package
        const packageToUpdate = testPackages.find(pkg => pkg._id === editingPackage);
        if (packageToUpdate) {
          const updatedPackage = await updateTestPackage(editingPackage, {
            name: packageToUpdate.name,
            description: packageToUpdate.description,
            price: packageToUpdate.price,
            discountPrice: packageToUpdate.discountPrice,
            category: packageToUpdate.category,
            priority: packageToUpdate.priority,
            active: packageToUpdate.active
          });
          
          setTestPackages(testPackages.map(pkg => 
            pkg._id === editingPackage ? updatedPackage : pkg
          ));
        }
        setEditingPackage(null);
      } else {
        // Add new package
        if (!newPackage.name.trim()) {
          alert('لطفاً نام پکیج را وارد کنید.');
          return;
        }
        
        const createdPackage = await createTestPackage(newPackage);
        setTestPackages([...testPackages, createdPackage]);
        
        setNewPackage({
          name: '',
          description: '',
          price: 0,
          discountPrice: 0,
          category: 'common',
          priority: 1,
          active: true
        });
      }
      
      setShowPackageForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در ذخیره پکیج');

    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = (id: string) => {
    const packageToEdit = testPackages.find(pkg => pkg._id === id);
    if (packageToEdit) {
      setEditingPackage(id);
      setShowPackageForm(true);
    }
  };

  const cancelEditing = () => {
    setEditingPackage(null);
    setShowPackageForm(false);
    setNewPackage({
      name: '',
      description: '',
      price: 0,
      discountPrice: 0,
      category: 'common',
      priority: 1,
      active: true
    });
    // Reload packages to reset any unsaved changes
    loadTestPackages();
  };

  const handleToggleStatus = async (id: string) => {
    try {
      setError(null);
      const updatedPackage = await toggleTestPackageStatus(id);
      setTestPackages(testPackages.map(pkg => 
        pkg._id === id ? updatedPackage : pkg
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در تغییر وضعیت پکیج');

    }
  };

  const handleDeletePackage = async (id: string) => {
    if (!window.confirm('آیا از حذف این پکیج اطمینان دارید؟')) {
      return;
    }
    
    try {
      setError(null);
      await deleteTestPackage(id);
      setTestPackages(testPackages.filter(pkg => pkg._id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در حذف پکیج');

    }
  };

  const currentPackage = editingPackage !== null
    ? testPackages.find(pkg => pkg._id === editingPackage)
    : newPackage;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fa-IR').format(price) + ' تومان';
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'common': return 'عمومی';
      case 'specialized': return 'تخصصی';
      case 'discount': return 'تخفیف دار';
      case 'emergency': return 'فوری';
      default: return category;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-gray-600">در حال بارگذاری پکیج ها...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">مدیریت پکیج های آزمایش</h1>
          <button 
            onClick={() => {
              setEditingPackage(null);
              setShowPackageForm(!showPackageForm);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {showPackageForm && !editingPackage ? 'انصراف' : 'افزودن پکیج جدید'}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <button
              onClick={() => setError(null)}
              className="absolute top-0 bottom-0 left-0 px-4 py-3"
            >
              <span className="sr-only">بستن</span>
              ✕
            </button>
          </div>
        )}
        
        {showPackageForm && (
          <div className="mb-8 bg-gray-50 p-6 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">
              {editingPackage !== null ? 'ویرایش پکیج' : 'افزودن پکیج جدید'}
            </h2>
            <form onSubmit={addOrUpdatePackage}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام پکیج</label>
                  <input
                    type="text"
                    name="name"
                    value={currentPackage?.name || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">دسته بندی</label>
                  <select
                    name="category"
                    value={currentPackage?.category || 'common'}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="common">عمومی</option>
                    <option value="specialized">تخصصی</option>
                    <option value="discount">تخفیف دار</option>
                    <option value="emergency">فوری</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات</label>
                  <textarea
                    name="description"
                    value={currentPackage?.description || ''}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">قیمت اصلی (تومان)</label>
                  <input
                    type="number"
                    name="price"
                    value={currentPackage?.price || 0}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">قیمت با تخفیف (تومان)</label>
                  <input
                    type="number"
                    name="discountPrice"
                    value={currentPackage?.discountPrice || 0}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">اولویت نمایش</label>
                  <input
                    type="number"
                    name="priority"
                    value={currentPackage?.priority || 1}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
                  <select
                    name="active"
                    value={currentPackage?.active ? 'true' : 'false'}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="true">فعال</option>
                    <option value="false">غیرفعال</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-2">
                {editingPackage !== null && (
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="ml-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    انصراف
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {submitting 
                    ? 'در حال ذخیره...' 
                    : editingPackage !== null 
                      ? 'به‌روزرسانی پکیج' 
                      : 'افزودن پکیج'
                  }
                </button>
              </div>
            </form>
          </div>
        )}
        
        {testPackages.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500 text-lg">هیچ پکیج آزمایشی یافت نشد.</div>
            <button
              onClick={() => setShowPackageForm(true)}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              افزودن اولین پکیج
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testPackages.map(pkg => (
            <div 
              key={pkg._id} 
              className={`border rounded-lg shadow-sm overflow-hidden ${pkg.active ? '' : 'opacity-60'}`}
            >
              <div className="bg-gray-100 p-4">
                <div className="flex justify-between items-start">
                  <h2 className="text-lg font-semibold">{pkg.name}</h2>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    pkg.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {pkg.active ? 'فعال' : 'غیرفعال'}
                  </span>
                </div>
                <div className="mt-1 text-sm text-gray-500">
                  دسته: {getCategoryLabel(pkg.category)}
                </div>
              </div>
              
              <div className="p-4">
                <p className="text-sm text-gray-600 min-h-[60px]">{pkg.description}</p>
                
                <div className="mt-4 flex justify-between items-center">
                  <div>
                    <div className="text-gray-500 text-sm line-through">{formatPrice(pkg.price)}</div>
                    <div className="text-blue-600 font-bold">{formatPrice(pkg.discountPrice)}</div>
                  </div>
                  <div className="text-sm text-gray-500">
                    اولویت: {pkg.priority}
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end space-x-2">
                  <button 
                    onClick={() => handleToggleStatus(pkg._id)}
                    className={`ml-2 px-2 py-1 rounded text-xs ${
                      pkg.active 
                        ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {pkg.active ? 'غیرفعال کردن' : 'فعال کردن'}
                  </button>
                  <button 
                    onClick={() => startEditing(pkg._id)}
                    className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                  >
                    ویرایش
                  </button>
                  <button 
                    onClick={() => handleDeletePackage(pkg._id)}
                    className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                  >
                    حذف
                  </button>
                </div>
              </div>
            </div>
                      ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestPackages;
